import { useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { AuthType } from "../../../ducks/auth";
import { useAsset } from "../../../hooks/useAsset";
import {
  validateEmail,
  validatePassword,
  validatePhoneNumber,
} from "../../../utils/auth";
import { Button } from "../../atoms/Button";
import { Input } from "../../atoms/Input";
import FormTemplate from "../../templates/FormTemplate";

type Props = {
  type?: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
  handleAuthValidationError: (
    isValidPhoneNumber: boolean,
    isValidEmail: boolean,
    isValidPassword: boolean,
  ) => void;
  handleSubmit: (
    name: string,
    phoneNumber: string,
    email: string,
    password: string,
    passwordConfirmation: string,
  ) => void;
};

export const RegisterForm: React.FC<Props> = ({
  type,
  name,
  email,
  phoneNumber,
  handleAuthValidationError,
  handleSubmit,
}) => {
  const [hasPhoneNumberError, setHasPhoneNumberError] =
    useState<boolean>(false);
  const [hasEmailError, setHasEmailError] = useState<boolean>(false);
  const [hasPasswordError, setHasPasswordError] = useState<boolean>(false);

  const { showSms } = useAsset();
  const schema = useMemo(() => {
    return z
      .object({
        name: z.string().min(1),
        phoneNumber: z.string().min(1),
        email: z.string().min(type !== undefined ? 1 : 0),
        password: z.string().min(1),
        passwordConfirmation: z.string().min(1),
      })
      .refine((data) => data.password === data.passwordConfirmation);
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    any,
    undefined
  > | null>(null);

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      defaultValues={{
        name: name ?? "",
        phoneNumber: phoneNumber ?? "",
        email: email ?? "",
        password: "",
        passwordConfirmation: "",
      }}
      onSubmit={({
        name,
        phoneNumber,
        email,
        password,
        passwordConfirmation,
      }) => {
        setHasPhoneNumberError(false);
        setHasEmailError(false);
        setHasPasswordError(false);

        const isValidPhoneNumber = validatePhoneNumber(phoneNumber);
        const isValidEmail = showSms ? validateEmail(email) : true;
        const isValidPassword = validatePassword(password);

        if (isValidPhoneNumber && isValidEmail && isValidPassword) {
          handleSubmit(
            name,
            phoneNumber,
            email,
            password,
            passwordConfirmation,
          );
        } else {
          if (!isValidPhoneNumber) setHasPhoneNumberError(true);
          if (!isValidEmail) setHasEmailError(true);
          if (!isValidPassword) setHasPasswordError(true);

          handleAuthValidationError(
            isValidPhoneNumber,
            isValidEmail,
            isValidPassword,
          );
        }
      }}
    >
      <Message>※印は必須項目です。</Message>
      <ContentWrapper>
        <Input
          name="name"
          type="text"
          label="名前"
          labelBorderBottom
          required
          dataWovnIgnore={true}
        />
      </ContentWrapper>
      <ContentWrapper>
        <Input
          name="phoneNumber"
          type="text"
          label="電話番号"
          labelBorderBottom
          required
          helperText="半角英数ハイフン無しで入力してください。"
          hasError={hasPhoneNumberError}
          disabled={
            type === AuthType.PhoneNumber.valueOf() && phoneNumber !== undefined
          }
          dataWovnIgnore={true}
        />
      </ContentWrapper>
      {showSms && (
        <ContentWrapper>
          <Input
            name="email"
            type="email"
            label="メールアドレス"
            labelExtraText="ログインIDとして使用します。"
            helperText="半角英数ハイフン無しで入力してください。"
            labelBorderBottom
            required
            hasError={hasEmailError}
            disabled={type === AuthType.Email.valueOf() && email !== undefined}
            dataWovnIgnore={true}
          />
        </ContentWrapper>
      )}
      <TitleWrapper>パスワード</TitleWrapper>
      <ContentWrapper>
        <Input
          name="password"
          type="password"
          label="パスワード"
          labelBorderBottom
          required
          helperText={`半角英数記号8文字以上24文字以内で、英字・数字・記号のうち2種類以上を混在させてください。\n
          使用可能な記号 ! " # $ % & ' ( ) * + , - . / : ; < = > ? @ [ \\ ] ^ _ \` { | } ~ \n
          ※スペースはご使用いただけません`}
          hasError={hasPasswordError}
          isNewPassword
          dataWovnIgnore={true}
        />
      </ContentWrapper>
      <ContentWrapper>
        <Input
          name="passwordConfirmation"
          type="password"
          label="確認のためパスワードを再入力してください"
          labelBorderBottom
          required
          hasError={hasPasswordError}
          isNewPassword
          dataWovnIgnore={true}
        />
      </ContentWrapper>

      <ButtonWrapper>
        <Button
          btnStyle="primary"
          text="登録内容の確認"
          type="submit"
          rounded="md"
        />
      </ButtonWrapper>
    </FormTemplate>
  );
};

const ContentWrapper = styled.div`
  margin: 0 0 30px;
  input {
  }
`;

const Message = styled.div`
  text-align: left;
  margin-bottom: 26px;
  font-size: 14px;
  font-weight: 200;
  font-family: "Hiragino Sans";
`;

const TitleWrapper = styled.div`
  font-size: 20px;
  padding: 43px 0 38px 0;
  display: flex;
  font-weight: 600;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 56px 0 12px;
  button {
    height: auto;
    line-height: 1;
    font-weight: 600;
  }
`;
